import React, { FC, ReactNode, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import {
  PoolSizeAdjustmentDirection,
  PoolSizeAdjustmentInput,
  PoolSizeAdjustmentIssue,
  PoolSizeAdjustmentReason,
  resolvePoolSizeAdjustment,
} from 'shared';
import { twMerge } from 'tailwind-merge';

import { ArrowNarrowDirection } from '../../../../assets/icons/arrow-narrow-icon';
import { ArrowNarrowIcon, GavelIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { FormInput } from '../../../../components/input/index';
import { HeightReveal } from '../../../../components/motion/index';
import { useReactForm } from '../../../../hooks/index';
import {
  createFormalAdjustmentSchema,
  parseShareAmount,
  PoolSizeContext,
} from '../adjust-pool-size.validation';
import { AdjustPoolSizeFooter } from '../components/modal-footer';
import { PoolSizeRow } from '../components/pool-size-row';

const directionCardStyles: Record<
  PoolSizeAdjustmentDirection,
  { card: string; description: string; icon: string; title: string }
> = {
  [PoolSizeAdjustmentDirection.INCREASE]: {
    card: 'bg-forest-25 ring-forest-500',
    icon: 'text-forest-600',
    title: 'text-forest-600',
    description: 'text-forest-500',
  },
  [PoolSizeAdjustmentDirection.DECREASE]: {
    card: 'bg-fireside-25 ring-fireside-600',
    icon: 'text-fireside-600',
    title: 'text-fireside-600',
    description: 'text-fireside-500',
  },
};

const colorTransitionClassName = 'transition-colors duration-200 motion-reduce:transition-none';

type DirectionCardProps = {
  arrow: ArrowNarrowDirection;
  description: ReactNode;
  direction: PoolSizeAdjustmentDirection;
  isSelected: boolean;
  onSelect: (direction: PoolSizeAdjustmentDirection) => void;
  title: ReactNode;
};

const DirectionCard: FC<DirectionCardProps> = ({
  arrow,
  description,
  direction,
  isSelected,
  onSelect,
  title,
}) => {
  const styles = directionCardStyles[direction];

  return (
    <button
      aria-checked={isSelected}
      // An inset ring is the design's inside stroke: it keeps the card at its padded size.
      className={twMerge(
        'flex flex-1 flex-col items-start rounded-lg px-4 py-3 text-left ring-1 ring-inset transition duration-200 motion-reduce:transition-none',
        isSelected ? styles.card : 'bg-white ring-gray-100 hover:ring-gray-200',
      )}
      onClick={() => onSelect(direction)}
      role="radio"
      type="button"
    >
      {/* Drawn in `currentColor`, so the arrow fades between colours with the text. */}
      <ArrowNarrowIcon
        className={twMerge(colorTransitionClassName, isSelected ? styles.icon : 'text-gray-700')}
        direction={arrow}
        iconColor="currentColor"
      />
      <span className="flex flex-col">
        <span
          className={twMerge(
            'text-base font-semibold',
            colorTransitionClassName,
            isSelected ? styles.title : 'text-gray-700',
          )}
        >
          {title}
        </span>
        <span
          className={twMerge(
            '!text-label-md font-medium',
            colorTransitionClassName,
            isSelected ? styles.description : 'text-gray-400',
          )}
        >
          {description}
        </span>
      </span>
    </button>
  );
};

export type FormalAdjustmentStepProps = {
  context: PoolSizeContext;
  isPending: boolean;
  onBack?: () => void;
  onCancel: () => void;
  onSubmit: (input: PoolSizeAdjustmentInput) => void;
};

export const FormalAdjustmentStep: FC<FormalAdjustmentStepProps> = ({
  context,
  isPending,
  onBack,
  onCancel,
  onSubmit,
}) => {
  const { currentPoolSize, grantedShares } = context;
  const schema = useMemo(
    () => createFormalAdjustmentSchema({ currentPoolSize, grantedShares }),
    [currentPoolSize, grantedShares],
  );
  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { isValid },
  } = useReactForm({ schema, defaultValues: { sharesCount: '' } });
  const { direction, sharesCount } = useWatch({ control });

  const resolution = direction
    ? resolvePoolSizeAdjustment({
        ...context,
        input: {
          reason: PoolSizeAdjustmentReason.FORMAL_ADJUSTMENT,
          direction,
          sharesCount: parseShareAmount(sharesCount),
        },
      })
    : null;
  // A decrease larger than the pool has no size to preview (it would read "-500 Shares"); its
  // error says why.
  const hasAmount =
    !!resolution &&
    resolution.issue !== PoolSizeAdjustmentIssue.INVALID_AMOUNT &&
    resolution.newPoolSize >= 0;

  const handleDirectionSelect = (nextDirection: PoolSizeAdjustmentDirection) => {
    setValue('direction', nextDirection);
    // The entered amount can turn invalid with the direction (a decrease below the granted
    // shares); revalidate it only once there is one, so an empty field shows no error yet.
    void trigger(sharesCount ? undefined : 'direction');
  };

  const handleConfirm = handleSubmit((values) =>
    onSubmit({
      reason: PoolSizeAdjustmentReason.FORMAL_ADJUSTMENT,
      direction: values.direction,
      sharesCount: parseShareAmount(values.sharesCount),
    }),
  );

  return (
    <>
      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full gap-2 rounded-md bg-sunbeam-50 p-3">
          <GavelIcon />
          <div className="flex flex-1 flex-col gap-2">
            <span className="text-sm font-[550] text-gray-800">
              <AppFormattedMessage id="LEGAL_NOTICE" />
            </span>
            <div className="flex flex-col gap-4 text-xs font-[450] text-gray-700">
              <p>
                <AppFormattedMessage id="POOL_ADJUSTMENT_LEGAL_NOTICE" />
              </p>
              <p>
                <AppFormattedMessage id="POOL_ADJUSTMENT_APPROVALS_REMINDER" />
              </p>
            </div>
          </div>
        </div>

        <div className="flex w-full flex-col gap-2">
          <span
            className="flex min-h-8 items-center py-1 text-xs font-[450] text-gray-700"
            id="adjust-pool-size-direction"
          >
            <AppFormattedMessage id="WHAT_WOULD_YOU_LIKE_TO_DO" />
          </span>
          <div
            aria-labelledby="adjust-pool-size-direction"
            className="flex gap-4"
            role="radiogroup"
          >
            <DirectionCard
              arrow={ArrowNarrowDirection.UP}
              description={<AppFormattedMessage id="ADD_SHARES_TO_POOL" />}
              direction={PoolSizeAdjustmentDirection.INCREASE}
              isSelected={direction === PoolSizeAdjustmentDirection.INCREASE}
              onSelect={handleDirectionSelect}
              title={<AppFormattedMessage id="INCREASE" />}
            />
            <DirectionCard
              arrow={ArrowNarrowDirection.DOWN}
              description={<AppFormattedMessage id="REMOVE_SHARES_FROM_POOL" />}
              direction={PoolSizeAdjustmentDirection.DECREASE}
              isSelected={direction === PoolSizeAdjustmentDirection.DECREASE}
              onSelect={handleDirectionSelect}
              title={<AppFormattedMessage id="DECREASE" />}
            />
          </div>
        </div>

        <PoolSizeRow
          label={<AppFormattedMessage id="CURRENT_POOL_SIZE" />}
          sharesCount={currentPoolSize}
        />

        {/* The reveal carries its own 16px top spacing (cancelling the column gap), so the space
            above it grows in with the content instead of appearing at once. */}
        <HeightReveal className="-mt-4" isShown={!!direction}>
          <div className="flex w-full flex-col gap-4 pt-4">
            <div className="flex w-full flex-col gap-3 rounded-lg border border-gray-100 bg-white p-4">
              {/* Keyed by direction, so switching direction fades the new wording in. */}
              <span
                className="flex min-h-8 items-center py-1 text-sm font-[450] text-gray-700 duration-200 animate-in fade-in-0 motion-reduce:animate-none"
                key={direction}
              >
                <AppFormattedMessage
                  id={
                    direction === PoolSizeAdjustmentDirection.INCREASE
                      ? 'AMOUNT_OF_SHARES_TO_ADD'
                      : 'AMOUNT_OF_SHARES_TO_DECREASE'
                  }
                />
              </span>
              <FormInput
                control={control}
                name="sharesCount"
                numberOnly
                placeholder={<AppFormattedMessage id="NO_OF_SHARES" />}
                shouldFormatNumber
              />
            </div>
            <PoolSizeRow
              delta={hasAmount ? resolution.delta : 0}
              label={<AppFormattedMessage id="NEW_POOL_SIZE" />}
              sharesCount={hasAmount ? resolution.newPoolSize : currentPoolSize}
            />
          </div>
        </HeightReveal>
      </div>
      <AdjustPoolSizeFooter
        confirmLabel={<AppFormattedMessage id="ADJUST_POOL_SIZE" />}
        isConfirmDisabled={!isValid}
        isPending={isPending}
        onBack={onBack}
        onCancel={onCancel}
        onConfirm={handleConfirm}
      />
    </>
  );
};
